import React, { useCallback, useState } from 'react';
import Icon from './Icon';
import './DataTable.css';

const STATUS_RANK = {
  behind: 0,
  warning: 0,
  danger: 0,
  inactive: 0,
  denied: 0,
  rejected: 0,
  returned: 1,
  needs_link: 1,
  'needs-link': 1,
  fair: 1,
  watch: 1,
  pending: 1,
  draft: 2,
  neutral: 2,
  'not-required': 2,
  not_required: 2,
  linked: 3,
  active: 3,
  approved: 3,
  good: 3,
  success: 3
};

function getStatusRank(status) {
  const key = String(status || '').trim().toLowerCase();
  return STATUS_RANK[key] ?? 2;
}

function defaultRender(value) {
  return value === null || value === undefined || value === '' ? '—' : value;
}

function resolveColumnValue(column, row) {
  if (typeof column.render === 'function') {
    return column.render(row);
  }

  if (column.key) {
    return row?.[column.key];
  }

  return null;
}

function renderSkeletonRows(count, columnCount) {
  return Array.from({ length: Math.max(0, count) }, (_, rowIndex) => (
    <tr className="data-table-skeleton-row" aria-hidden="true" key={`data-table-skeleton-row-${rowIndex}`}>
      {Array.from({ length: Math.max(1, columnCount) }, (_, columnIndex) => (
        <td key={columnIndex}>
          <span className="data-table-skeleton-cell" />
        </td>
      ))}
    </tr>
  ));
}

function renderMobileSkeletonCards(count) {
  return Array.from({ length: Math.max(0, count) }, (_, index) => (
    <div className="data-table-mobile-skeleton-card" aria-hidden="true" key={`data-table-mobile-skeleton-${index}`}>
      <span className="data-table-skeleton-cell" />
      <span className="data-table-skeleton-cell" />
      <span className="data-table-skeleton-cell" />
    </div>
  ));
}

function renderOverflowDetails(columns, row) {
  if (!columns.length) {
    return null;
  }

  return (
    <dl className="data-table-details">
      {columns.map(column => (
        <div className="data-table-detail" key={column.key || column.label}>
          <dt>{column.label}</dt>
          <dd>{defaultRender(resolveColumnValue(column, row))}</dd>
        </div>
      ))}
    </dl>
  );
}

function flattenChildren(children) {
  return React.Children.toArray(children).flatMap(child => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      return flattenChildren(child.props.children);
    }
    return child;
  });
}

function isInteractiveTarget(target) {
  return Boolean(target?.closest?.('button, a, input, select, textarea, summary, details, [role="button"]'));
}

/**
 * Structural wrapper for director-style data tables.
 *
 * Renders the table-card div, role-table, and thead from a `columns` array.
 * Row rendering is left to the caller via children — keeping complex per-row
 * logic (status pills, action buttons) local to each panel.
 *
 * @param {Array<{label: string, className?: string, key?: string, sortable?: boolean, sortLabel?: string}>} columns  Column header definitions
 * @param {string}  [tableClassName]  Extra class applied to the <table> element
 * @param {string}  [emptyMessage]    Message shown when isEmpty is true
 * @param {boolean} [isEmpty]         When true, renders the emptyMessage row
 * @param {string|null} [sortColumn]      The column.key currently sorted, or null/undefined for none.
 * @param {'asc'|'desc'|'none'} [sortDirection='none']  Direction for the active sortColumn.
 * @param {(columnKey: string) => void} [onSort]  Called with column.key when a sortable header is
 *   activated. DataTable never sorts `rows` itself -- the caller owns the actual sort.
 * @param {boolean} [loading=false]   When true, renders loadingRowCount skeleton rows instead of
 *   real rows/empty state, and disables sortable header buttons.
 * @param {number}  [loadingRowCount=5]  Number of skeleton rows to render while loading.
 * @param {string}  [aria-label]  Accessible name for the horizontally-scrollable table
 *   region (the desktop <table> can overflow past the viewport above --bp-md; the region
 *   is a keyboard-reachable, discoverable scroll container per the WAI-ARIA scrolling
 *   region pattern). Defaults to a generic label; callers with a more specific table
 *   identity (e.g. "KPI scorecard") should pass their own.
 * @param {React.ReactNode} children  <tr> elements rendered inside <tbody>
 */
function DataTable({
  columns,
  rows = null,
  rowKey = (row, index) => row?.id ?? index,
  rowClassName = () => '',
  rowStatus = () => '',
  rowLabel = () => '',
  onRowClick = null,
  renderExpanded = null,
  groupBy = null,
  groupLabel = group => group,
  maxVisibleColumns = 5,
  defaultSort = 'status',
  tableClassName = '',
  emptyMessage = 'No records found.',
  emptyState = null,
  isEmpty = false,
  sortColumn = null,
  sortDirection = 'none',
  onSort = null,
  loading = false,
  loadingRowCount = 5,
  'aria-label': ariaLabel = 'Data table, scroll horizontally for more columns',
  children
}) {
  const hasStructuredRows = Array.isArray(rows);
  const visibleColumns = hasStructuredRows ? columns.slice(0, maxVisibleColumns) : columns;
  const overflowColumns = hasStructuredRows ? columns.slice(maxVisibleColumns) : [];

  const sortedRows = hasStructuredRows
    ? [...rows].sort((left, right) => {
      if (defaultSort !== 'status') {
        return 0;
      }
      const rankDelta = getStatusRank(rowStatus(left)) - getStatusRank(rowStatus(right));
      if (rankDelta !== 0) {
        return rankDelta;
      }
      return String(rowLabel(left)).localeCompare(String(rowLabel(right)));
    })
    : [];

  const groupedRows = hasStructuredRows && typeof groupBy === 'function'
    ? sortedRows.reduce((groups, row) => {
      const group = groupBy(row) || 'Ungrouped';
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      groups.get(group).push(row);
      return groups;
    }, new Map())
    : null;

  // Sprint 3E.2: per-row expansion is React-state-driven instead of
  // the previous native <details> element. The native approach
  // rendered the expanded panel INSIDE the first <td>, which trapped
  // the panel inside that column's width — operators on
  // /location-scorecard saw expanded KPI detail crushed into the
  // Score Status column. The new mechanism renders the expanded
  // content as a sibling <tr> with <td colSpan={...}>, so the panel
  // spans the full table width and the other column cells remain
  // aligned in the data row above.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const renderRows = (items, groupPrefix = '') => items.flatMap((row, index) => {
    const key = rowKey(row, index);
    const composedKey = `${groupPrefix}${key}`;
    const status = rowStatus(row);
    const label = rowLabel(row) || 'row details';
    const details = typeof renderExpanded === 'function'
      ? renderExpanded(row, overflowColumns)
      : renderOverflowDetails(overflowColumns, row);
    const isExpandable = Boolean(details);
    const isExpanded = isExpandable && expandedKeys.has(composedKey);
    // colspan for the expanded sibling row spans every visible
    // column plus the hidden status-rank sort column (if present).
    const expandedRowColSpan = visibleColumns.length + (hasStructuredRows ? 1 : 0);
    const expandedRowId = `data-table-detail-${composedKey}`;

    const dataRow = (
      <tr
        key={`${composedKey}-row`}
        className={`data-table-row ${isExpandable ? 'data-table-row-expandable' : ''} ${isExpanded ? 'data-table-row-expanded' : ''} ${rowClassName(row) || ''}`.trim()}
        onClick={(event) => {
          if (typeof onRowClick === 'function') {
            onRowClick(row, event);
          }
        }}
      >
        {visibleColumns.map((column, columnIndex) => (
          <td key={column.key || column.label} className={column.className || undefined}>
            {columnIndex === 0 && isExpandable ? (
              <button
                type="button"
                className={`data-table-expander${isExpanded ? ' data-table-expander--open' : ''}`}
                aria-expanded={isExpanded}
                aria-controls={expandedRowId}
                aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleExpanded(composedKey);
                }}
              >
                <span className="data-table-expander-icon" aria-hidden>›</span>
              </button>
            ) : null}
            {defaultRender(resolveColumnValue(column, row))}
          </td>
        ))}
        {status ? <td className="data-table-status-rank" aria-hidden>{getStatusRank(status)}</td> : null}
      </tr>
    );

    if (!isExpanded) {
      return [dataRow];
    }

    // Sibling row hosting the full-width detail panel. The <td>
    // colSpan covers every visible column + the sort column, so the
    // panel content spans the entire table.
    return [
      dataRow,
      (
        <tr
          key={`${composedKey}-detail`}
          id={expandedRowId}
          className="data-table-detail-row"
        >
          <td colSpan={expandedRowColSpan} className="data-table-detail-cell">
            <div className="data-table-expander-panel">{details}</div>
          </td>
        </tr>
      )
    ];
  });

  const renderStructuredMobileCards = (items, groupPrefix = '') => items.map((row, index) => {
    const key = rowKey(row, index);
    const status = rowStatus(row);
    const label = rowLabel(row) || 'Record';
    const details = typeof renderExpanded === 'function'
      ? renderExpanded(row, overflowColumns)
      : null;

    return (
      <article
        className={`data-table-mobile-card ${rowClassName(row) || ''}`.trim()}
        data-status={String(status || 'neutral').toLowerCase()}
        key={`${groupPrefix}${key}`}
        tabIndex={typeof onRowClick === 'function' ? 0 : undefined}
        role={typeof onRowClick === 'function' ? 'button' : undefined}
        onClick={(event) => {
          if (typeof onRowClick === 'function' && !isInteractiveTarget(event.target)) {
            onRowClick(row, event);
          }
        }}
        onKeyDown={(event) => {
          if (typeof onRowClick === 'function' && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onRowClick(row, event);
          }
        }}
      >
        <div className="data-table-mobile-card-header">
          <strong>{label}</strong>
          {status ? <span>{String(status).replace(/[-_]/g, ' ')}</span> : null}
        </div>
        <dl className="data-table-mobile-fields">
          {columns.map(column => (
            <div className="data-table-mobile-field" key={column.key || column.label}>
              <dt>{column.label}</dt>
              <dd>{defaultRender(resolveColumnValue(column, row))}</dd>
            </div>
          ))}
        </dl>
        {details ? (
          <details className="data-table-mobile-expander">
            <summary>More details</summary>
            <div>{details}</div>
          </details>
        ) : null}
      </article>
    );
  });

  const legacyRows = hasStructuredRows ? [] : flattenChildren(children).filter(React.isValidElement);
  const renderLegacyMobileCards = () => legacyRows.map((row, rowIndex) => {
    const cells = flattenChildren(row.props.children).filter(React.isValidElement);
    const isGroupRow = cells.length === 1 && (cells[0].props.colSpan || String(row.props.className || '').includes('group-row'));

    if (isGroupRow) {
      return (
        <div className="data-table-mobile-group" key={row.key || `legacy-group-${rowIndex}`}>
          {cells[0].props.children}
        </div>
      );
    }

    const titleCell = cells.find(cell => cell.type === 'th') || cells[0];

    return (
      <article className={`data-table-mobile-card ${row.props.className || ''}`.trim()} key={row.key || `legacy-row-${rowIndex}`}>
        <div className="data-table-mobile-card-header">
          <strong>{defaultRender(titleCell?.props?.children)}</strong>
        </div>
        <dl className="data-table-mobile-fields">
          {cells.map((cell, cellIndex) => (
            <div className="data-table-mobile-field" key={`${row.key || rowIndex}-${cell.key || cellIndex}`}>
              <dt>{columns[cellIndex]?.label || `Field ${cellIndex + 1}`}</dt>
              <dd>{defaultRender(cell.props.children)}</dd>
            </div>
          ))}
        </dl>
      </article>
    );
  });

  const renderHeaderCell = (column) => {
    const isSortable = Boolean(column.sortable && typeof onSort === 'function');

    if (!isSortable) {
      return (
        <th key={column.label} scope="col" className={column.className || undefined}>
          {column.label}
        </th>
      );
    }

    const isActive = sortColumn === column.key;
    const activeDirection = isActive && (sortDirection === 'asc' || sortDirection === 'desc') ? sortDirection : null;
    const ariaSortValue = activeDirection === 'asc' ? 'ascending' : activeDirection === 'desc' ? 'descending' : undefined;
    const sortLabel = column.sortLabel || column.label;
    const iconName = activeDirection === 'asc' ? 'ChevronUp' : activeDirection === 'desc' ? 'ChevronDown' : 'ChevronsUpDown';

    return (
      <th key={column.label} scope="col" className={column.className || undefined} aria-sort={ariaSortValue}>
        <button
          type="button"
          className={`data-table-sort-button${isActive ? ' data-table-sort-button--active' : ''}`}
          onClick={() => onSort(column.key)}
          disabled={loading}
          aria-label={`Sort by ${sortLabel}`}
        >
          <span className="data-table-sort-label" aria-hidden="true">{column.label}</span>
          <Icon name={iconName} size="sm" className="data-table-sort-icon" />
        </button>
      </th>
    );
  };

  return (
    <div className="table-card" tabIndex={0} role="region" aria-label={ariaLabel}>
      <table className={`role-table${tableClassName ? ` ${tableClassName}` : ''}`} aria-busy={loading || undefined}>
        <thead>
          <tr>
            {visibleColumns.map(renderHeaderCell)}
            {hasStructuredRows ? <th scope="col" className="data-table-status-rank" aria-hidden>Sort</th> : null}
          </tr>
        </thead>
        <tbody>
          {loading ? renderSkeletonRows(loadingRowCount, visibleColumns.length) : (
            <>
              {hasStructuredRows && groupedRows ? (
                Array.from(groupedRows.entries()).map(([group, items]) => (
                  <React.Fragment key={group}>
                    <tr className="data-table-group-row">
                      <td colSpan={visibleColumns.length}>{groupLabel(group, items)}</td>
                    </tr>
                    {renderRows(items, `${group}:`)}
                  </React.Fragment>
                ))
              ) : hasStructuredRows ? renderRows(sortedRows) : children}
              {(isEmpty || (hasStructuredRows && rows.length === 0)) && (
                <tr>
                  <td colSpan={visibleColumns.length}>
                    {emptyState || emptyMessage}
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
      <div className="data-table-mobile-list" aria-label="Table rows">
        {loading ? renderMobileSkeletonCards(loadingRowCount) : (
          <>
            {hasStructuredRows && groupedRows ? (
              Array.from(groupedRows.entries()).map(([group, items]) => (
                <React.Fragment key={group}>
                  <div className="data-table-mobile-group">{groupLabel(group, items)}</div>
                  {renderStructuredMobileCards(items, `${group}:`)}
                </React.Fragment>
              ))
            ) : hasStructuredRows ? renderStructuredMobileCards(sortedRows) : renderLegacyMobileCards()}
            {(isEmpty || (hasStructuredRows && rows.length === 0)) && (
              <div className="data-table-mobile-empty">
                {emptyState || emptyMessage}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DataTable;
